import axios from "axios";
import { config } from '../../../config/Config';

const orgMgmtUrl = `${config.serverBaseUrl}/v1/web/org-mgmt`;

const authHeaders = (accessToken) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`
});

export const getAllOrgs = async ({ accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: orgMgmtUrl,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching all orgs:', error.response?.data || error.message);
        throw error;
    }
};

export const getOrgById = async ({ accessToken, id }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${orgMgmtUrl}/${id}`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching org by ID:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Just the org's display name, readable by any MEMBER of it. getOrgById returns the whole record and
 * needs the ORG page at READ_ONLY, which a plain member does not hold — so anything that only wants
 * to NAME the workspace must come here, or it falls back to showing a raw id.
 */
export const getOrgNameById = async ({ accessToken, id }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${orgMgmtUrl}/${id}/name`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching org name by ID:', error.response?.data || error.message);
        throw error;
    }
};

export const createOrg = async ({ accessToken, orgData }) => {
    console.log(orgData);
    try {
        const response = await axios({
            method: 'post',
            url: orgMgmtUrl,
            headers: authHeaders(accessToken),
            data: orgData
        });
        return response.data;
    } catch (error) {
        console.error('Error creating org:', error.response?.data || error.message);
        throw error;
    }
};

export const updateOrg = async ({ accessToken, id, orgData }) => {
    try {
        const response = await axios({
            method: 'put',
            url: orgMgmtUrl,
            headers: authHeaders(accessToken),
            data: { id, ...orgData }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating org:', error.response?.data || error.message);
        throw error;
    }
};

export const archiveOrg = async ({ accessToken, id }) => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${orgMgmtUrl}/${id}/archive`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error archiving org:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Freezes an org (ACTIVE -> SUSPENDED). Reversible counterpart of archive: grants stay intact but
 * every write beneath the org is refused server-side, and its members are bounced off the app.
 * Super-user only on the backend.
 */
export const suspendOrg = async ({ accessToken, id }) => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${orgMgmtUrl}/${id}/suspend`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error suspending org:', error.response?.data || error.message);
        throw error;
    }
};

/** Lifts a suspension (SUSPENDED -> ACTIVE). Refused for an archived org — archive is terminal. */
export const reactivateOrg = async ({ accessToken, id }) => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${orgMgmtUrl}/${id}/activate`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error reactivating org:', error.response?.data || error.message);
        throw error;
    }
};

export const getOrgsByDomain = async ({ accessToken, domain }) => {
    try {
        const response = await axios.get(`${orgMgmtUrl}/domain-lookup`, {
            params: { domain },
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error looking up orgs by domain:', error.response?.data || error.message);
        throw error;
    }
};

export const selfOnboardOrg = async ({ accessToken, orgData }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${orgMgmtUrl}/self-onboard`,
            headers: authHeaders(accessToken),
            data: orgData
        });
        return response.data;
    } catch (error) {
        console.error('Error self-onboarding org:', error.response?.data || error.message);
        throw error;
    }
};

export const requestOrgAccess = async ({ accessToken, orgId, requestData }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${orgMgmtUrl}/${orgId}/access-request`,
            headers: authHeaders(accessToken),
            data: requestData
        });
        return response.data;
    } catch (error) {
        console.error('Error requesting org access:', error.response?.data || error.message);
        throw error;
    }
};

// Context for a prospective member's onboarding join request: the org's games and the
// roles they may request (disciplines + Game Co-Owner). Returns empty lists if the
// caller's email domain doesn't match the org.
export const getOrgJoinContext = async ({ accessToken, orgId }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${orgMgmtUrl}/${orgId}/join-context`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching org join context:', error.response?.data || error.message);
        throw error;
    }
};